/**
 * SVG chart helpers for the briefing: a frame that fits lat/lon points into a viewBox (same
 * local projection as the radar scope), a north arrow with magnetic north, and a scale bar.
 * Everything returns SVG markup strings; no DOM.
 */
import { createOrigin, NM_TO_M, toScene, type LatLon } from '@atc/core';

export const esc = (s: string): string => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export interface Frame {
  x(lat: number, lon: number): number;
  y(lat: number, lon: number): number;
  pxPerNm: number;
  width: number;
  height: number;
}

/** North-up frame, uniform scale, centred on the points' extent (at least 2 nm across). */
export function fitFrame(points: readonly LatLon[], width: number, height: number, pad: number): Frame {
  const o = createOrigin(points[0]?.lat ?? 0, points[0]?.lon ?? 0, 0);
  const en = (lat: number, lon: number) => { const p = toScene(o, lat, lon, 0); return { e: p.x, n: -p.z }; };
  let minE = Infinity, maxE = -Infinity, minN = Infinity, maxN = -Infinity;
  for (const p of points) {
    const q = en(p.lat, p.lon);
    minE = Math.min(minE, q.e); maxE = Math.max(maxE, q.e); minN = Math.min(minN, q.n); maxN = Math.max(maxN, q.n);
  }
  if (!points.length) { minE = maxE = minN = maxN = 0; }
  const spanE = Math.max(maxE - minE, 2 * NM_TO_M), spanN = Math.max(maxN - minN, 2 * NM_TO_M);
  const cE = (minE + maxE) / 2, cN = (minN + maxN) / 2;
  const scale = Math.min((width - 2 * pad) / spanE, (height - 2 * pad) / spanN);
  return {
    x: (lat, lon) => width / 2 + (en(lat, lon).e - cE) * scale,
    y: (lat, lon) => height / 2 - (en(lat, lon).n - cN) * scale,
    pxPerNm: scale * NM_TO_M,
    width,
    height,
  };
}

/** True north arrow with a dashed magnetic north line (variation positive east). */
export function northArrow(x: number, y: number, magVarDeg: number): string {
  return `<g transform="translate(${x},${y})">
    <g transform="rotate(${magVarDeg.toFixed(1)})"><line x1="0" y1="10" x2="0" y2="-26" stroke="#8b95a5" stroke-width="1.2" stroke-dasharray="3 2"/><text x="10" y="-22" class="bc-sb">MN</text></g>
    <path d="M0,-32 L6,-14 L0,-18 L-6,-14 Z" fill="#dfe5ee"/><line x1="0" y1="-18" x2="0" y2="12" stroke="#dfe5ee" stroke-width="1.5"/>
    <text x="0" y="-38" text-anchor="middle" class="bc-sb">N</text></g>`;
}

/** Scale bar in nautical miles (metres under 1 nm), at most `maxPx` long. */
export function scaleBar(f: Frame, x: number, y: number, maxPx = 160): string {
  const steps = [0.1, 0.25, 0.5, 1, 2, 5, 10, 20, 50, 100];
  const nm = [...steps].reverse().find((s) => s * f.pxPerNm <= maxPx) ?? 0.1;
  const px = nm * f.pxPerNm;
  const label = nm < 1 ? `${Math.round(nm * 1852)} m` : `${nm} nm`;
  return `<g transform="translate(${x},${y})"><path d="M0,-6V0H${px.toFixed(1)}V-6" fill="none" stroke="#8b95a5" stroke-width="1.5"/><text x="${(px / 2).toFixed(1)}" y="-10" text-anchor="middle" class="bc-sb">${label}</text></g>`;
}

/** Shared chart styles; class names are prefixed because SVG styles apply to the whole page. */
export const CHART_STYLE = `<style>
  .bc-sb, .bc-ft { font: 11px B612, Arial, sans-serif; fill: #8b95a5; }
  .bc-ttl { font: 700 20px B612, Arial, sans-serif; fill: #dfe5ee; }
  .bc-fx { font: 12px "B612 Mono", monospace; fill: #cfe7cf; }
  .bc-rs { font: 11px "B612 Mono", monospace; fill: #ffcf8a; }
  .bc-hd { font: 11px "B612 Mono", monospace; fill: #9fb7c9; }
  .bc-rw { font: 700 13px "B612 Mono", monospace; fill: #e8edf2; }
  .bc-rwm { font: 11px "B612 Mono", monospace; fill: #8b95a5; }
  .bc-ad { fill: #0d1319; stroke: #243040; stroke-width: 1; }
  .bc-apron { fill: #1b2430; }
  .bc-twy { fill: none; stroke: #3b4757; stroke-linecap: round; stroke-linejoin: round; }
  .bc-bldg { fill: #2c3644; }
</style>`;
